import { Tab } from "@headlessui/react";
import { useSelector } from "react-redux";
import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";

import { states } from "js/placeholderData/states";
import { useRowPagination } from "hooks/useRowPagination";
import { useBreadcrumb } from "hooks/useBreadcrumb";
import { getFormsByCategories } from "service/forms/getForms";
import { getCompanyNameByBlockCode } from "js/formatting";
import RowPagination from "components/General/RowPagination";
import SimpleDataRow from "components/General/SimpleDataRow";
import EverlakeLoadingOverlay from "components/General/EverlakeLoadingOverlay";

export default function Forms() {
  const { t } = useTranslation();

  const [allForms, setAllForms] = useState([]);
  const [policyChanges, setPolicyChanges] = useState([]);
  const [financialRequests, setFinancialRequests] = useState([]);
  const [claims, setClaims] = useState([]);
  const [other, setOther] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  const [policyChangesSubCategory, setPolicyChangesSubCategory] =
    useState("all");
  const [policyChangesState, setPolicyChangesState] = useState("all");
  const [policyChangesCompany, setPolicyChangesCompany] = useState("all");
  const [policyChangesProductType, setPolicyChangesProductType] =
    useState("all");

  const [financialRequestSubCategory, setFinancialRequestSubCategory] =
    useState("all");
  const [financialRequestState, setFinancialRequestState] = useState("all");
  const [financialRequestCompany, setFinancialRequestCompany] = useState("all");
  const [financialRequestProductType, setFinancialRequestProductType] =
    useState("all");

  const [claimsSubCategory, setClaimsSubCategory] = useState("all");
  const [claimsState, setClaimssState] = useState("all");
  const [claimsCompany, setClaimsCompany] = useState("all");
  const [claimsProductType, setClaimsProductType] = useState("all");

  const [otherSubCategory, setOthersSubCategory] = useState("all");
  const [otherState, setOthersState] = useState("all");
  const [otherCompany, setOthersCompany] = useState("all");
  const [otherProductType, setOthersProductType] = useState("all");
  const [selectedTabIndex, setSelectedTabIndex] = useState(0);
  async function getForm(portalSubCategory, state) {
    setIsLoading(true);
    await getFormsByCategories("agent", portalSubCategory, state)
      .then((respone) => {
        setAllForms(respone);
        setIsLoading(false);
        setClaims(respone.filter((x) => x.portalCategoryNumber === 1));
        setFinancialRequests(
          respone.filter((x) => x.portalCategoryNumber === 2)
        );
        setPolicyChanges(respone.filter((x) => x.portalCategoryNumber === 3));
        setOther(respone.filter((x) => x.portalCategoryNumber === 4));
      })
      .catch((error) => {
        setIsLoading(false);
        console.log("Error", error);
      });
  }
  useEffect(() => {
    //Get all forms form portal Api
    getForm(0, "");
  }, []);

  const formsfiltering = (
    category,
    subCategory,
    state,
    company,
    productType
  ) => {
    if (allForms?.length > 0) {
      let filteredForms = allForms.filter(
        (x) => parseInt(x.portalCategoryNumber) === parseInt(category)
      );
      if (subCategory !== "all" && !Number.isInteger(subCategory)) {
        filteredForms = filteredForms.filter(
          (x) => parseInt(x.portalSubCategoryNumber) === parseInt(subCategory)
        );
      }
      if (state !== "all") {
        filteredForms = filteredForms.filter(
          (x) => x.state?.toLowerCase() === state?.toLowerCase()
        );
      }
      if (company !== "all") {
        filteredForms = filteredForms.filter(
          (x) => x.company?.toLowerCase() === company?.toLowerCase()
        );
      }
      if (productType !== "all")
        filteredForms = filteredForms.filter(
          (x) => x.productType?.toLowerCase() === productType?.toLowerCase()
        );
      switch (category) {
        case 1: {
          setClaims(filteredForms);
          break;
        }
        case 2: {
          setFinancialRequests(filteredForms);
          break;
        }
        case 3: {
          setPolicyChanges(filteredForms);
          break;
        }
        case 4: {
          setOther(filteredForms);
          break;
        }
        default:
          break;
      }
    }
  };

  const {
    rowsForDisplay: policyChangesForms,
    rowPaginationProps: policyChangesFormsPaginationProps,
  } = useRowPagination(policyChanges);

  const {
    rowsForDisplay: financialRequestForms,
    rowPaginationProps: financialRequestFormsPaginationProps,
  } = useRowPagination(financialRequests);

  const {
    rowsForDisplay: claimsForms,
    rowPaginationProps: claimsFormsPaginationProps,
  } = useRowPagination(claims);

  const {
    rowsForDisplay: otherForms,
    rowPaginationProps: otherFormsPaginationProps,
  } = useRowPagination(other);

  const { user } = useSelector((s) => s.user);

  useBreadcrumb(
    user.userId
      ? [
          { name: "Home", url: "/dashboard" },
          { name: "Services and Claims", url: "/services-claims" },
          { name: "Forms", url: "#" },
        ]
      : [
          { name: "Home", url: "/" },
          { name: "Forms", url: "#" },
        ]
  );

  return (
    <>
      {/* <div className="container mb-8">
        <h1>Forms </h1>
        <p>Forms needed to manage customer policies</p>
        <hr />
      </div> */}

      <div className="container mb-8">
        <h1>{t("Forms-title")}</h1>
        <p>{t("Forms-subtitle")}</p>
      </div>
      <EverlakeLoadingOverlay
        isLoading={isLoading}
        loadingText="Please wait while we retrieve forms... "
      >
        <div className="container ui-component-spacing">
          <Tab.Group>
            <div className="flex-row md:flex">
              <div className="ui-card md:flex-grow px-2 p-1 md:px-4">
                <Tab.List className="flex flex-row whitespace-nowrap">
                  <Tab
                    className={`text-sm mx-3 pt-3 pb-2 mb-1 md:text-base md:mx-4 lg:text-lg ${
                      selectedTabIndex === 0 ? "" : "text-ui-blue"
                    }`}
                    onClick={() => {
                      setSelectedTabIndex(0);
                    }}
                  >
                    Policy Changes
                  </Tab>
                  <Tab
                    className={`text-sm mx-3 pt-3 pb-2 mb-1 md:text-base md:mx-4 lg:text-lg ${
                      selectedTabIndex === 1 ? "" : "text-ui-blue"
                    }`}
                    onClick={() => {
                      setSelectedTabIndex(1);
                    }}
                  >
                    Financial Requests
                  </Tab>
                  <Tab
                    className={`text-sm mx-3 pt-3 pb-2 mb-1 md:text-base md:mx-4 lg:text-lg ${
                      selectedTabIndex === 2 ? "" : "text-ui-blue"
                    }`}
                    onClick={() => {
                      setSelectedTabIndex(2);
                    }}
                  >
                    Claims
                  </Tab>
                  <Tab
                    className={`text-sm mx-3 pt-3 pb-2 mb-1 md:text-base md:mx-4 lg:text-lg ${
                      selectedTabIndex === 3 ? "" : "text-ui-blue"
                    }`}
                    onClick={() => {
                      setSelectedTabIndex(3);
                    }}
                  >
                    Others
                  </Tab>
                </Tab.List>
              </div>
            </div>

            <Tab.Panels className="mt-6">
              <Tab.Panel>
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-1 md:grid-cols-2 lg:grid-cols-4">
                  <div className="my-4 md:my-0 md:flex md:items-center">
                    <label
                      htmlFor="policyChangesFormsId"
                      className="mr-2 whitespace-nowrap"
                    >
                      Sub Category
                    </label>
                    <select
                      id="policyChangesFormsId"
                      className="w-full"
                      value={policyChangesSubCategory}
                      onChange={(e) => {
                        setPolicyChangesSubCategory(e.target.value);
                        formsfiltering(
                          3,
                          e.target.value,
                          policyChangesState,
                          policyChangesCompany,
                          policyChangesProductType
                        );
                      }}
                    >
                      <option value="all">All</option>
                      <option value="4">Beneficiary</option>
                      <option value="5">Agent</option>
                      <option value="6">Assignment</option>
                      <option value="8">Reinstatement</option>
                      <option value="12">Contract</option>
                    </select>
                  </div>
                  <div className="my-4 md:my-0 md:flex md:items-center">
                    <label
                      htmlFor="policyChangesFormsStateId"
                      className="mr-2 whitespace-nowrap"
                    >
                      State
                    </label>
                    <select
                      id="policyChangesFormsStateId"
                      className="w-full"
                      value={policyChangesState}
                      onChange={(e) => {
                        setPolicyChangesState(e.target.value);
                        formsfiltering(
                          3,
                          policyChangesSubCategory,
                          e.target.value,
                          policyChangesCompany,
                          policyChangesProductType
                        );
                      }}
                    >
                      <option value="all">All</option>
                      {states.map((state, idx) => {
                        return (
                          <option
                            key={`policy Changes Forms State selection - ${idx}`}
                            value={state.abbreviation}
                          >
                            {state.name}
                          </option>
                        );
                      })}
                    </select>
                  </div>
                  <div className="my-4 md:my-0 md:flex md:items-center">
                    <label
                      htmlFor="policyChangesFormsCompanyId"
                      className="mr-2 whitespace-nowrap"
                    >
                      Product Type
                    </label>
                    <select
                      id="policyChangesFormsCompanyId"
                      className="w-full"
                      value={policyChangesProductType}
                      onChange={(e) => {
                        setPolicyChangesProductType(e.target.value);
                        formsfiltering(
                          3,
                          policyChangesSubCategory,
                          policyChangesState,
                          policyChangesCompany,
                          e.target.value
                        );
                      }}
                    >
                      <option value="all">All</option>
                      <option value="term">Term</option>
                      <option value="annuity">Annuity</option>
                    </select>
                  </div>
                  <div className="my-4 md:my-0 md:flex md:items-center">
                    <label
                      htmlFor="formCategoryId"
                      className="mr-2 whitespace-nowrap"
                    >
                      Company
                    </label>
                    <select
                      id="formCategoryId"
                      className="w-full"
                      value={policyChangesCompany}
                      onChange={(e) => {
                        setPolicyChangesCompany(e.target.value);
                        formsfiltering(
                          3,
                          policyChangesSubCategory,
                          policyChangesState,
                          e.target.value,
                          policyChangesProductType
                        );
                      }}
                    >
                      <option value="all">All</option>
                      <option value="elic">
                        {getCompanyNameByBlockCode("ELIC")}
                      </option>
                      <option value="eac">
                        {getCompanyNameByBlockCode("EAC")}
                      </option>
                      <option value="lbl">
                        {getCompanyNameByBlockCode("LBL")}
                      </option>
                      <option value="slic">
                        {getCompanyNameByBlockCode("SLIC")}
                      </option>
                    </select>
                  </div>
                </div>
                {policyChangesForms?.map((form, idx) => (
                  <SimpleDataRow
                    key={`policy Changes Forms Company change - ${idx}`}
                    data={form}
                    type="form"
                  />
                ))}

                <RowPagination {...policyChangesFormsPaginationProps} />
              </Tab.Panel>

              <Tab.Panel>
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-1 md:grid-cols-2 lg:grid-cols-4">
                  <div className="my-4 md:my-0 md:flex md:items-center">
                    <label
                      htmlFor="financialRequestFormsId"
                      className="mr-2 whitespace-nowrap"
                    >
                      Sub Category
                    </label>
                    <select
                      id="financialRequestFormsId"
                      className="w-full"
                      value={financialRequestSubCategory}
                      onChange={(e) => {
                        setFinancialRequestSubCategory(e.target.value);
                        formsfiltering(
                          2,
                          e.target.value,
                          financialRequestState,
                          financialRequestCompany,
                          financialRequestProductType
                        );
                      }}
                    >
                      <option value="all">All</option>
                      <option value="3">Billing</option>
                      <option value="13">Contract</option>
                    </select>
                  </div>
                  <div className="my-4 md:my-0 md:flex md:items-center">
                    <label
                      htmlFor="financialRequestFormsStateId"
                      className="mr-2 whitespace-nowrap"
                    >
                      State
                    </label>
                    <select
                      id="financialRequestFormsStateId"
                      className="w-full"
                      value={financialRequestState}
                      onChange={(e) => {
                        setFinancialRequestState(e.target.value);
                        formsfiltering(
                          2,
                          financialRequestSubCategory,
                          e.target.value,
                          financialRequestCompany,
                          financialRequestProductType
                        );
                      }}
                    >
                      <option value="all">All</option>
                      {states.map((state, idx) => {
                        return (
                          <option
                            key={`financial Request Forms State selection - ${idx}`}
                            value={state.abbreviation}
                          >
                            {state.name}
                          </option>
                        );
                      })}
                    </select>
                  </div>
                  <div className="my-4 md:my-0 md:flex md:items-center">
                    <label
                      htmlFor="inancialRequestFormsProductTypeId"
                      className="mr-2 whitespace-nowrap"
                    >
                      Product Type
                    </label>
                    <select
                      id="inancialRequestFormsProductTypeId"
                      className="w-full"
                      value={financialRequestProductType}
                      onChange={(e) => {
                        setFinancialRequestProductType(e.target.value);
                        formsfiltering(
                          2,
                          financialRequestSubCategory,
                          financialRequestState,
                          financialRequestCompany,
                          e.target.value
                        );
                      }}
                    >
                      <option value="all">All</option>
                      <option value="term">Term</option>
                      <option value="annuity">Annuity</option>
                    </select>
                  </div>
                  <div className="my-4 md:my-0 md:flex md:items-center">
                    <label
                      htmlFor="financialRequestFormsCompanyId"
                      className="mr-2 whitespace-nowrap"
                    >
                      Company
                    </label>
                    <select
                      id="financialRequestFormsCompanyId"
                      className="w-full"
                      value={financialRequestCompany}
                      onChange={(e) => {
                        setFinancialRequestCompany(e.target.value);
                        formsfiltering(
                          2,
                          financialRequestSubCategory,
                          financialRequestState,
                          e.target.value,
                          financialRequestProductType
                        );
                      }}
                    >
                      <option value="all">All</option>
                      <option value="elic">
                        {getCompanyNameByBlockCode("ELIC")}
                      </option>
                      <option value="eac">
                        {getCompanyNameByBlockCode("EAC")}
                      </option>
                      <option value="lbl">
                        {getCompanyNameByBlockCode("LBL")}
                      </option>
                      <option value="slic">
                        {getCompanyNameByBlockCode("SLIC")}
                      </option>
                    </select>
                  </div>
                </div>
                {financialRequestForms?.map((form, idx) => (
                  <SimpleDataRow
                    key={`forms for financial request - ${idx}`}
                    data={form}
                    type="form"
                  />
                ))}

                <RowPagination {...financialRequestFormsPaginationProps} />
              </Tab.Panel>

              <Tab.Panel>
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-1 md:grid-cols-2 lg:grid-cols-4">
                  <div className="my-4 md:my-0 md:flex md:items-center">
                    <label
                      htmlFor="claimsFormsId"
                      className="mr-2 whitespace-nowrap"
                    >
                      Sub Category
                    </label>
                    <select
                      id="claimsFormsId"
                      className="w-full"
                      value={claimsSubCategory}
                      onChange={(e) => {
                        setClaimsSubCategory(e.target.value);
                        formsfiltering(
                          1,
                          e.target.value,
                          claimsState,
                          claimsCompany,
                          claimsProductType
                        );
                      }}
                    >
                      <option value="all">All</option>
                      <option value="1">Direct Deposit</option>
                      <option value="2">Information Authorization</option>
                    </select>
                  </div>
                  <div className="my-4 md:my-0 md:flex md:items-center">
                    <label
                      htmlFor="claimsFormsStateId"
                      className="mr-2 whitespace-nowrap"
                    >
                      State
                    </label>
                    <select
                      id="claimsFormsStateId"
                      className="w-full"
                      value={claimsState}
                      onChange={(e) => {
                        setClaimssState(e.target.value);
                        formsfiltering(
                          1,
                          claimsSubCategory,
                          e.target.value,
                          claimsCompany,
                          claimsProductType
                        );
                      }}
                    >
                      <option value="all">All</option>
                      {states.map((state, idx) => {
                        return (
                          <option
                            key={`claims Forms State selection - ${idx}`}
                            value={state.abbreviation}
                          >
                            {state.name}
                          </option>
                        );
                      })}
                    </select>
                  </div>
                  <div className="my-4 md:my-0 md:flex md:items-center">
                    <label
                      htmlFor="claimsFormsProductTypeId"
                      className="mr-2 whitespace-nowrap"
                    >
                      Product Type
                    </label>
                    <select
                      id="claimsFormsProductTypeId"
                      className="w-full"
                      value={claimsProductType}
                      onChange={(e) => {
                        setClaimsProductType(e.target.value);
                        formsfiltering(
                          1,
                          claimsSubCategory,
                          claimsState,
                          claimsCompany,
                          e.target.value
                        );
                      }}
                    >
                      <option value="all">All</option>
                      <option value="term">Term</option>
                      <option value="annuity">Annuity</option>
                    </select>
                  </div>
                  <div className="my-4 md:my-0 md:flex md:items-center">
                    <label
                      htmlFor="claimsFormsCompanyId"
                      className="mr-2 whitespace-nowrap"
                    >
                      Company
                    </label>
                    <select
                      id="claimsFormsCompanyId"
                      className="w-full"
                      value={claimsCompany}
                      onChange={(e) => {
                        setClaimsCompany(e.target.value);
                        formsfiltering(
                          1,
                          claimsSubCategory,
                          claimsState,
                          e.target.value,
                          claimsProductType
                        );
                      }}
                    >
                      <option value="all">All</option>
                      <option value="elic">
                        {getCompanyNameByBlockCode("ELIC")}
                      </option>
                      <option value="eac">
                        {getCompanyNameByBlockCode("EAC")}
                      </option>
                      <option value="lbl">
                        {getCompanyNameByBlockCode("LBL")}
                      </option>
                      <option value="slic">
                        {getCompanyNameByBlockCode("SLIC")}
                      </option>
                    </select>
                  </div>
                </div>
                {claimsForms?.map((form, idx) => (
                  <SimpleDataRow
                    key={`forms for claims - ${idx}`}
                    data={form}
                    type="form"
                  />
                ))}

                <RowPagination {...claimsFormsPaginationProps} />
              </Tab.Panel>

              <Tab.Panel>
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-1 md:grid-cols-2 lg:grid-cols-4">
                  <div className="my-4 md:my-0 md:flex md:items-center">
                    <label
                      htmlFor="otherFormsId"
                      className="mr-2 whitespace-nowrap"
                    >
                      Sub Category
                    </label>
                    <select
                      id="otherFormsId"
                      className="w-full"
                      value={otherSubCategory}
                      onChange={(e) => {
                        setOthersSubCategory(e.target.value);
                        formsfiltering(
                          4,
                          e.target.value,
                          otherState,
                          otherCompany,
                          otherProductType
                        );
                      }}
                    >
                      <option value="all">All</option>
                      <option value="11">Information Authorization</option>
                      <option value="9">Contract</option>
                      <option value="7">Address</option>
                      <option value="10">Fax</option>
                    </select>
                  </div>
                  <div className="my-4 md:my-0 md:flex md:items-center">
                    <label
                      htmlFor="otherFormsStateId"
                      className="mr-2 whitespace-nowrap"
                    >
                      State
                    </label>
                    <select
                      id="otherFormsStateId"
                      className="w-full"
                      value={otherState}
                      onChange={(e) => {
                        setOthersState(e.target.value);
                        formsfiltering(
                          4,
                          otherSubCategory,
                          e.target.value,
                          otherCompany,
                          otherProductType
                        );
                      }}
                    >
                      <option value="all">All</option>
                      {states.map((state, idx) => {
                        return (
                          <option
                            key={`other Forms State selection - ${idx}`}
                            value={state.abbreviation}
                          >
                            {state.name}
                          </option>
                        );
                      })}
                    </select>
                  </div>
                  <div className="my-4 md:my-0 md:flex md:items-center">
                    <label
                      htmlFor="otherFormsProducTypeId"
                      className="mr-2 whitespace-nowrap"
                    >
                      Product Type
                    </label>
                    <select
                      id="otherFormsProducTypeId"
                      value={otherProductType}
                      className="w-full"
                      onChange={(e) => {
                        setOthersProductType(e.target.value);
                        formsfiltering(
                          4,
                          otherSubCategory,
                          otherState,
                          otherCompany,
                          e.target.value
                        );
                      }}
                    >
                      <option value="all">All</option>
                      <option value="term">Term</option>
                      <option value="annuity">Annuity</option>
                    </select>
                  </div>
                  <div className="my-4 md:my-0 md:flex md:items-center">
                    <label
                      htmlFor="otherFormsCompanyId"
                      className="mr-2 whitespace-nowrap"
                    >
                      Company
                    </label>
                    <select
                      id="otherFormsCompanyId"
                      className="w-full"
                      value={otherCompany}
                      onChange={(e) => {
                        setOthersCompany(e.target.value);
                        formsfiltering(
                          4,
                          otherSubCategory,
                          otherState,
                          e.target.value,
                          otherProductType
                        );
                      }}
                    >
                      <option value="all">All</option>
                      <option value="elic">
                        {getCompanyNameByBlockCode("ELIC")}
                      </option>
                      <option value="eac">
                        {getCompanyNameByBlockCode("EAC")}
                      </option>
                      <option value="lbl">
                        {getCompanyNameByBlockCode("LBL")}
                      </option>
                      <option value="slic">
                        {getCompanyNameByBlockCode("SLIC")}
                      </option>
                    </select>
                  </div>
                </div>
                {otherForms?.map((form, idx) => (
                  <SimpleDataRow
                    key={`other forms - ${idx}`}
                    data={form}
                    type="form"
                  />
                ))}

                <RowPagination {...otherFormsPaginationProps} />
              </Tab.Panel>
            </Tab.Panels>
          </Tab.Group>
        </div>
      </EverlakeLoadingOverlay>
    </>
  );
}
